import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient, APIResponse } from '../client';
import type {
  Supplier,
  SupplierDocument,
  PurchaseRequest,
  PurchaseOrder,
  GoodsReceivedNote,
  SupplierComparison,
} from '../queries/purchasing.queries';

// ================================
// SUPPLIER MUTATIONS
// ================================

export interface CreateSupplierInput {
  name: string;
  code?: string;
  contactPerson: string;
  phone: string;
  alternatePhone?: string;
  email: string;
  alternateEmail?: string;
  website?: string;
  address: string;
  city?: string;
  province?: string;
  postalCode?: string;
  country?: string;
  taxId?: string;
  registrationNumber?: string;
  bankAccount?: {
    bankName?: string;
    accountName?: string;
    accountNumber?: string;
    branchName?: string;
  };
  paymentTerms?: string;
  paymentTermsDays?: number;
  creditLimit?: number;
  leadTimeDays?: number;
  minimumOrderValue?: number;
  deliveryDays?: string[];
  suppliedIngredients?: string[];
  suppliedCategories?: string[];
  isPreferred?: boolean;
  notes?: string;
}

export function useCreateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateSupplierInput) => {
      const response = await apiClient.post<APIResponse<{ supplier: Supplier }>>(
        '/purchasing/suppliers',
        data
      );
      return response.data.data.supplier;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

export function useUpdateSupplier(supplierId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<CreateSupplierInput>) => {
      const response = await apiClient.patch<APIResponse<{ supplier: Supplier }>>(
        `/purchasing/suppliers/${supplierId}`,
        data
      );
      return response.data.data.supplier;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
      queryClient.invalidateQueries({ queryKey: ['supplier', supplierId] });
    },
  });
}

export function useDeleteSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (supplierId: string) => {
      const response = await apiClient.delete<APIResponse<{ supplier: Supplier }>>(
        `/purchasing/suppliers/${supplierId}`
      );
      return response.data.data.supplier;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

// ================================
// SUPPLIER DOCUMENT MUTATIONS
// ================================

export interface UploadSupplierDocumentInput {
  supplierId: string;
  type: 'contract' | 'invoice' | 'certificate' | 'correspondence' | 'other';
  name: string;
  fileUrl: string;
  fileSize?: number;
  mimeType?: string;
  expiryDate?: string;
  notes?: string;
}

export function useUploadSupplierDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ supplierId, ...data }: UploadSupplierDocumentInput) => {
      const response = await apiClient.post<APIResponse<{ document: SupplierDocument }>>(
        `/purchasing/suppliers/${supplierId}/documents`,
        data
      );
      return response.data.data.document;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['supplier-documents', variables.supplierId] });
      queryClient.invalidateQueries({ queryKey: ['supplier', variables.supplierId] });
    },
  });
}

export function useDeleteSupplierDocument() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ supplierId, docId }: { supplierId: string; docId: string }) => {
      await apiClient.delete(`/purchasing/suppliers/${supplierId}/documents/${docId}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['supplier-documents', variables.supplierId] });
      queryClient.invalidateQueries({ queryKey: ['supplier', variables.supplierId] });
    },
  });
}

// ================================
// PRICE ALERT MUTATIONS
// ================================

export function useAcknowledgePriceAlert() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (alertId: string) => {
      const response = await apiClient.post(`/purchasing/price-alerts/${alertId}/acknowledge`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['price-alerts'] });
    },
  });
}

// ================================
// PURCHASE REQUEST MUTATIONS
// ================================

export interface PRLineItemInput {
  ingredientId?: string;
  customItemName?: string;
  customItemDescription?: string;
  isFreeFormItem?: boolean;
  quantity: number;
  unit: string;
  estimatedPrice?: number;
  suggestedQuantity?: number;
  forecastBasis?: string;
  reason?: string;
}

export interface CreatePRInput {
  branchId: string;
  items: PRLineItemInput[];
  urgency?: 'normal' | 'high' | 'urgent';
  urgencyReason?: string;
  requestedDeliveryDate?: string;
  notes?: string;
}

export function useCreatePurchaseRequest() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePRInput) => {
      const response = await apiClient.post<APIResponse<{ pr: PurchaseRequest }>>(
        '/purchasing/purchase-requests',
        data
      );
      return response.data.data.pr;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-requests'] });
    },
  });
}

export interface ModifyPRItemsInput {
  items: Array<{
    itemId: string;
    quantity: number;
    modificationReason: string;
  }>;
  hqModificationNotes?: string;
}

export function useModifyPRItems(prId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: ModifyPRItemsInput) => {
      const response = await apiClient.patch<APIResponse<{ pr: PurchaseRequest }>>(
        `/purchasing/purchase-requests/${prId}/items`,
        data
      );
      return response.data.data.pr;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-requests'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-request', prId] });
    },
  });
}

export function useApprovePurchaseRequest(prId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notes?: string) => {
      const response = await apiClient.patch<APIResponse<{ pr: PurchaseRequest }>>(
        `/purchasing/purchase-requests/${prId}/approve`,
        { notes }
      );
      return response.data.data.pr;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-requests'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-request', prId] });
    },
  });
}

export function useRejectPurchaseRequest(prId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reason: string) => {
      const response = await apiClient.patch<APIResponse<{ pr: PurchaseRequest }>>(
        `/purchasing/purchase-requests/${prId}/reject`,
        { reason }
      );
      return response.data.data.pr;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-requests'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-request', prId] });
    },
  });
}

// ================================
// PURCHASE ORDER MUTATIONS
// ================================

export interface POLineItemInput {
  ingredientId?: string;
  customItemName?: string;
  isFreeFormItem?: boolean;
  quantity: number;
  unit: string;
  unitPrice: number;
  sourceItems?: Array<{
    prId: string;
    prLineItemId: string;
    quantity: number;
    branchId: string;
  }>;
}

export interface SplitDeliveryInput {
  expectedDate: string;
  quantity: number;
  unit: string;
  notes?: string;
}

export interface SupplierComparisonInput {
  supplierId: string;
  totalPrice: number;
  leadTimeDays: number;
  performanceRating: number;
  wasSelected: boolean;
}

export interface CreatePOInput {
  prIds: string[];
  supplierId: string;
  deliveryBranchId: string;
  items: POLineItemInput[];
  taxRate?: number;
  shipping?: number;
  discount?: number;
  expectedDeliveryDate?: string;
  hasSplitDelivery?: boolean;
  splitDeliveries?: SplitDeliveryInput[];
  supplierComparison?: SupplierComparisonInput[];
  notes?: string;
  internalNotes?: string;
}

export function useCreatePurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreatePOInput) => {
      const response = await apiClient.post<APIResponse<{ po: PurchaseOrder }>>(
        '/purchasing/purchase-orders',
        data
      );
      return response.data.data.po;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-requests'] });
    },
  });
}

export function useSendPurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (poId: string) => {
      const response = await apiClient.patch<APIResponse<{ po: PurchaseOrder }>>(
        `/purchasing/purchase-orders/${poId}/send`
      );
      return response.data.data.po;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

export function useConfirmPurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (poId: string) => {
      const response = await apiClient.patch<APIResponse<{ po: PurchaseOrder }>>(
        `/purchasing/purchase-orders/${poId}/confirm`
      );
      return response.data.data.po;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

// ================================
// GRN MUTATIONS
// ================================

export interface GRNLineItemInput {
  poLineItemId: string;
  ingredientId?: string;
  customItemName?: string;
  isFreeFormItem?: boolean;
  orderedQuantity: number;
  receivedQuantity: number;
  unit: string;
  supplierBatchNumber?: string;
  internalBatchNumber?: string;
  expiryDate?: string;
  condition?: 'good' | 'damaged' | 'expired' | 'wrong_item' | 'quality_issue';
  qualityScore?: number;
  notes?: string;
}

export interface CreateGRNInput {
  poId: string;
  warehouseId: string;
  items: GRNLineItemInput[];
  deliveryNoteNumber?: string;
  invoiceNumber?: string;
  invoiceAmount?: number;
  photos?: string[];
  notes?: string;
}

export function useCreateGRN() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateGRNInput) => {
      const response = await apiClient.post<APIResponse<{ grn: GoodsReceivedNote }>>(
        '/purchasing/goods-received',
        data
      );
      return response.data.data.grn;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goods-received-notes'] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
    },
  });
}

export interface DiscrepancyDecisionInput {
  action: 'accepted' | 'rejected' | 'quarantine' | 'return_to_supplier';
  notes?: string;
  quarantineLocation?: string;
  quarantineReleaseDate?: string;
}

export function useGRNDiscrepancyDecision(grnId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ itemId, ...data }: DiscrepancyDecisionInput & { itemId: string }) => {
      const response = await apiClient.patch<APIResponse<{ grn: GoodsReceivedNote }>>(
        `/purchasing/goods-received/${grnId}/items/${itemId}/decision`,
        data
      );
      return response.data.data.grn;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goods-received-notes'] });
      queryClient.invalidateQueries({ queryKey: ['goods-received-note', grnId] });
    },
  });
}

export function useCompleteGRN(grnId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reviewNotes?: string) => {
      const response = await apiClient.patch<APIResponse<{ grn: GoodsReceivedNote }>>(
        `/purchasing/goods-received/${grnId}/complete`,
        { reviewNotes }
      );
      return response.data.data.grn;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['goods-received-notes'] });
      queryClient.invalidateQueries({ queryKey: ['goods-received-note', grnId] });
      queryClient.invalidateQueries({ queryKey: ['purchase-orders'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['suppliers'] });
    },
  });
}

// ================================
// SUPPLIER COMPARISON MUTATION
// ================================

export interface CompareSupplierInput {
  items: Array<{
    ingredientId: string;
    quantity: number;
    unit: string;
  }>;
  supplierIds?: string[];
}

export function useCompareSuppliers() {
  return useMutation({
    mutationFn: async (data: CompareSupplierInput) => {
      const response = await apiClient.post<APIResponse<{ comparisons: SupplierComparison[] }>>(
        '/purchasing/supplier-comparison',
        data
      );
      return response.data.data.comparisons;
    },
  });
}
